import type { HelpArticle } from '../../../components/help/types';

export const gettingStartedArticles: HelpArticle[] = [
  {
    id: 'getting-started-intro',
    title: 'Welcome to OHCS RMS',
    summary: 'An introduction to the OHCS Records Management System and its key features.',
    category: 'getting-started',
    order: 1,
    keywords: ['welcome', 'introduction', 'overview', 'getting started', 'first steps'],
    content: `
# Welcome to OHCS Records Management System

The OHCS Records Management System (RMS) is a comprehensive solution for managing documents, personnel files, and workflows within Ghana's civil service institutions.

## Key Features

- **Document Management**: Securely store, organize, and track all institutional documents
- **Personnel Files (PFiles)**: Manage staff records using the jacket-based system
- **Workflow Approvals**: Route documents through approval chains
- **Search & Reports**: Find documents instantly and generate reports
- **Security Classifications**: Control access based on document sensitivity

## Getting Started

1. Log in with your institutional credentials
2. Explore the dashboard to see an overview of your activity
3. Navigate using the sidebar to access different features
4. Take the interactive tour for a guided introduction

## Need Help?

- Click the Help button in the header anytime
- Press \`Shift + ?\` to view keyboard shortcuts
- Contact your institution's IT administrator for account issues
    `,
  },
  {
    id: 'getting-started-navigation',
    title: 'Navigating the System',
    summary: 'Learn how to navigate through the OHCS RMS interface effectively.',
    category: 'getting-started',
    order: 2,
    keywords: ['navigation', 'sidebar', 'menu', 'interface', 'ui'],
    content: `
# Navigating the System

The OHCS RMS interface is designed for easy navigation and efficient workflow.

## Main Navigation

### Sidebar
The sidebar on the left provides access to all major sections:
- **Dashboard**: Overview and quick access
- **Staff Records**: Document management
- **File Jackets**: Personnel file management
- **My Tasks**: Pending workflow items
- **Search**: Find documents
- **Reports**: Generate reports

### Header
The header provides:
- Institution logo and name
- Theme toggle (dark/light mode)
- Notification bell
- User profile menu

## Keyboard Navigation

- \`Ctrl + K\`: Quick search
- \`Shift + ?\`: Show all shortcuts
- \`Esc\`: Close modals and dialogs

## Breadcrumbs

When navigating through folders, use breadcrumbs at the top to:
- See your current location
- Jump back to any parent folder
- Navigate hierarchically
    `,
  },
  {
    id: 'getting-started-dashboard',
    title: 'Using Your Dashboard',
    summary: 'Understand the dashboard and how to use it effectively.',
    category: 'getting-started',
    order: 3,
    keywords: ['dashboard', 'overview', 'statistics', 'activity', 'tasks'],
    content: `
# Using Your Dashboard

The dashboard is your central hub for monitoring activity and accessing key information.

## Dashboard Sections

### Quick Statistics
View at-a-glance metrics:
- Total documents in your institution
- Pending tasks waiting for your action
- Documents completed this week
- Overdue items requiring attention

### Quick Actions
Common actions are available with one click:
- Create new record
- Search records
- View pending tasks

### Recent Records
See the most recently updated documents. Click any document to view its details.

### Pending Tasks
Documents waiting for your review or approval. Take action directly from the dashboard.

### Activity Feed
Track all recent activity in your institution:
- Document uploads
- Approvals and rejections
- Comments and updates

## Best Practices

1. Check your dashboard at the start of each day
2. Address overdue tasks promptly
3. Use quick actions for common workflows
4. Review activity feed for important updates
    `,
  },
];
